import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { enablePush, disablePush, getPushStatus, testPush, pushVerify } from "@/lib/push";

/**
 * Återanvändbar push-aktiverings/test-widget.
 * Är icke-admin-begränsad och passar in på Notifikationssidan så att
 * alla familjemedlemmar (även barn + extra-vuxna) kan aktivera och
 * testa sin egen push utan att behöva komma åt Settings.
 *
 * Kontrollerar att FCM-token verkligen är aktivt registrerad i backend
 * (inte bara att localStorage har en token) — om inte: visar
 * "Aktivera push"-knapp + toast med uppmaning.
 */
export function PushActivationCard() {
  const [status, setStatus] = useState(null);
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const refreshStatus = async () => {
    setVerifying(true);
    try {
      const s = await getPushStatus();
      setStatus(s);
      // Verifiera mot backend att token:en verkligen är aktiv
      const v = await pushVerify();
      setVerify(v);
      // Om vi har lokal token MEN backend säger inte registrerad/inaktiv → varna
      if (v && (!v.registered || !v.enabled) && s.permission === "granted") {
        toast.warning("Push verkar inte vara aktivt registrerat i backend — klicka 'Registrera om' för att fixa.");
      }
    } catch (e) {
      setStatus({ supported: false, permission: "denied", error: String(e?.message || e) });
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => { refreshStatus(); }, []);

  const onEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push-notiser aktiverade!");
      await refreshStatus();
    } catch (e) {
      toast.error(e?.message || "Kunde inte aktivera push");
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    if (!confirm("Stäng av push på denna enhet?")) return;
    setBusy(true);
    try {
      await disablePush();
      toast.success("Push avaktiverat på denna enhet");
      await refreshStatus();
    } catch (e) {
      toast.error(e?.message || "Kunde inte avaktivera");
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const res = await testPush();
      if (res?.sent > 0) {
        toast.success(`Testnotis skickad till ${res.sent} enhet(er) — kolla telefonens notiscentral!`);
      } else {
        toast.error("Inga enheter att skicka till. Aktivera push först.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Kunde inte skicka test");
    } finally {
      setTesting(false);
    }
  };

  if (!status) {
    return (
      <div className="card-soft p-3 mb-3 flex items-center gap-2 text-xs text-[color:var(--text-tertiary)]" data-testid="push-card-loading">
        <Loader2 size={14} className="animate-spin" /> Kontrollerar push-status…
      </div>
    );
  }

  const blocked = status.permission === "denied";
  // Aktiv = permission granted + lokalt har token + backend bekräftar registreringen
  const reallyActive = status.permission === "granted"
    && !!verify
    && verify.registered === true
    && verify.enabled === true;
  // "Mellanläge": permission granted men backend säger inte registrerad → trasig koppling
  const broken = status.permission === "granted" && verify && (!verify.registered || !verify.enabled);

  let iconBgClass = "bg-amber-100 text-amber-700";
  let StatusIcon = Bell;
  let statusLabel = "Inte aktiverad";
  if (reallyActive) { iconBgClass = "bg-emerald-100 text-emerald-700"; StatusIcon = CheckCircle2; statusLabel = "Aktiverad ✓"; }
  else if (blocked) { iconBgClass = "bg-red-100 text-red-700"; StatusIcon = AlertCircle; statusLabel = "Blockerad i webbläsaren"; }
  else if (broken) { iconBgClass = "bg-amber-100 text-amber-700"; StatusIcon = AlertCircle; statusLabel = "Registrering saknas — klicka 'Registrera om'"; }

  return (
    <div className="card-soft p-4 mb-3" data-testid="push-activation-card">
      <div className="flex items-start gap-3">
        <div className={`shrink-0 rounded-full p-2 ${iconBgClass}`}>
          <StatusIcon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-[color:var(--text-tertiary)] flex items-center gap-1">
            Push på denna enhet
            {verifying && <Loader2 size={10} className="animate-spin" />}
          </p>
          <p className="font-medium text-sm mt-0.5" data-testid="push-status-label">
            {statusLabel}
          </p>
          <p className="text-[11px] text-[color:var(--text-tertiary)] mt-1 leading-relaxed">
            {reallyActive && "Du får familjenotiser direkt på den här enheten. Skicka en test för att verifiera."}
            {!reallyActive && !blocked && !broken && (
              <>
                Push är inte aktiverad. När appen öppnas från hemskärmen frågar
                systemet automatiskt — eller klicka knappen nedan för att
                trigga aktiveringen manuellt. På iPhone måste appen läggas
                till på hemskärmen först (Dela → Lägg till på hemskärmen).
              </>
            )}
            {broken && (
              <>
                Webbläsaren tror att push är på, men backend har ingen aktiv
                registrering för den här enheten — token:en kan ha gått ut
                eller blivit deaktiverad. Klicka "Registrera om" för att
                hämta en ny FCM-token och koppla in enheten på nytt.
              </>
            )}
            {blocked && (
              <>
                Webbläsaren blockerar notiser för denna sida. Rätta i
                webbläsarens inställningar (Notiser → Tillåt för
                familjen.grindstugatan.se), sedan ladda om.
              </>
            )}
          </p>

          {/* Knapprad */}
          <div className="flex gap-2 mt-3 flex-wrap">
            {/* Aktivera / Registrera om — visas alltid om inte blockerad */}
            {!blocked && (
              <Button
                size="sm"
                onClick={onEnable}
                disabled={busy || !status.supported}
                data-testid={reallyActive ? "push-reregister-btn" : "push-enable-btn"}
                className={`${reallyActive ? "bg-white text-[color:var(--jarvis)] border border-[color:var(--jarvis)] hover:bg-[color:var(--jarvis)]/5" : "bg-[color:var(--jarvis)] hover:bg-[color:var(--jarvis)]/90 text-white"}`}
              >
                {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> :
                  reallyActive ? <RefreshCw size={14} className="mr-1.5" /> : <Bell size={14} className="mr-1.5" />}
                {reallyActive ? "Registrera om" : "Aktivera push"}
              </Button>
            )}
            {/* Test + Stäng av — bara när verkligen aktiv */}
            {reallyActive && (
              <>
                <Button
                  size="sm"
                  onClick={onTest}
                  disabled={testing}
                  data-testid="push-test-btn"
                  className="bg-[color:var(--jarvis)] hover:bg-[color:var(--jarvis)]/90 text-white"
                >
                  {testing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                  Skicka testnotis
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDisable}
                  disabled={busy}
                  data-testid="push-disable-btn"
                >
                  <BellOff size={14} className="mr-1.5" />
                  Stäng av
                </Button>
              </>
            )}
            {!status.supported && (
              <span className="text-[11px] text-red-700 flex items-center gap-1">
                <AlertCircle size={12} /> Push stöds inte i denna webbläsare
              </span>
            )}
          </div>

          {/* Debug-info */}
          {verify && verify.last_seen_at && reallyActive && (
            <p className="text-[10px] text-[color:var(--text-tertiary)] mt-2" data-testid="push-last-seen">
              Senast sedd: {new Date(verify.last_seen_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}
              {verify.label && ` · ${verify.label}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
